import React, { useEffect, useState } from 'react';

import { ReactEditor, useSlateStatic } from 'slate-react';

import application from 'application';
import serverInterface from 'server-interface';

import externalEventCodes from 'event-system/codes/external';
import internalEventCodes from 'event-system/codes/internal';

import { ReactComponent as ReloadIcon } from 'assets/images/reload-icon.svg';

import HelptapEditor from '../../helpers';

import nodeControls from '../../node-menu/control-types';

import BaseElement from '../base';

import './index.scss';

const progressTypes = {
  COPIES: 'Copies',
  BLOCKS: 'Blocks'
};

function TemplateCloner(props) {

  const element = props.element;
  const properties = element.properties;

  const editor = useSlateStatic();

  const elementPath = ReactEditor.findPath(editor, props.element);

  const [rootState, setRootState] = useState({
    progressType: null,
    total: 0,
    processed: 0
  });

  useEffect(function onLoad() {

    return initialize();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function determineProgressPercent(total, processed) {

    const percent = Math.round((rootState.processed / rootState.total) * 100);

    if (percent > 100) {
      return 100;
    }

    return percent;

  }

  function determineWorkingStatus(properties) {

    if (properties.clonerId === null) {
      return `Starting to import ${properties.template.name}...`;
    } else {
      if (rootState.progressType === null) {
        return `Importing ${properties.template.name}...`;
      } else if (rootState.progressType === progressTypes.COPIES) {
        const percent = determineProgressPercent(rootState.total, rootState.processed);
        return `Generating copies ${percent}%`;
      } else if (rootState.progressType === progressTypes.BLOCKS) {
        const percent = determineProgressPercent(rootState.total, rootState.processed);
        return `Generating blocks ${percent}%`;
      }
    }

  }

  async function createCloner() {

    HelptapEditor.setNodeBusy(editor, elementPath);

    try {

      const templateId = properties.template._id;
      const payload = {
        parentEntityId: properties.parentEntityId,
        parentEntityType: properties.parentEntityType,
        customization: properties.customization
      };

      const cloner = await serverInterface.startup.templates.createTemplateCloner(templateId, payload);

      HelptapEditor.setAttributes(editor, {
        properties: {
          ...properties,
          clonerId: cloner._id
        }
      }, elementPath);

      HelptapEditor.setNodeIdle(editor, elementPath);

    } catch (exception) {

      application.eventSystem.internal.emit(internalEventCodes.DISPLAY_SYSTEM_TOAST_MESSAGE, {
        message: `${properties.template.name} is not available for importing!`,
        seconds: 5,
        type: 'error'
      });
      application.dataManagers.get('TemplatesManager').tryRemove(properties.template._id);
      HelptapEditor.deleteNodeByPathV2(editor, elementPath);

    }

  }

  async function loadCloner() {

    try {

      const cloner = await serverInterface.startup.templates.getTemplateClonerById(properties.template._id, properties.clonerId);

      if (cloner.status === 'COMPLETED') {
        HelptapEditor.replaceNode(editor, elementPath, cloner.content);
      } else if (cloner.status === 'FAILED') {
        HelptapEditor.setAttributes(editor, {
          properties: {
            ...properties,
            retry: true,
            retryMessage: `Failed to import template ${properties.template.name}. Want to retry?`
          }
        }, elementPath);
      }

    } catch (exception) {

      HelptapEditor.setAttributes(editor, {
        properties: {
          ...properties,
          retry: true,
          retryMessage: `Importing template ${properties.template.name} expired. Want to retry?`
        }
      }, elementPath);

    }

  }

  function registerEventListeners() {

    function handleCloningCompleted(payload) {

      HelptapEditor.replaceNode(editor, elementPath, payload.cloner.content);

    }

    function handleCloningFailed(payload) {

      HelptapEditor.setAttributes(editor, {
        properties: {
          ...properties,
          retry: true,
          retryMessage: `Failed to import template ${properties.template.name}. Want to retry?`
        }
      }, elementPath);

    }

    function handleCloningProgress(payload) {

      setRootState(function updateRootState(rootState) {

        return {
          ...rootState,
          progressType: payload.progressType,
          total: payload.total,
          processed: payload.processed
        };

      });

    }

    application.eventSystem.internal.on(externalEventCodes.TEMPLATE_CLONING_COMPLETED, handleCloningCompleted);
    application.eventSystem.internal.on(externalEventCodes.TEMPLATE_CLONING_FAILED, handleCloningFailed);
    application.eventSystem.internal.on(externalEventCodes.TEMPLATE_CLONING_PROGRESS, handleCloningProgress);

    return function cleanup() {

      application.eventSystem.internal.off(externalEventCodes.TEMPLATE_CLONING_COMPLETED, handleCloningCompleted);
      application.eventSystem.internal.off(externalEventCodes.TEMPLATE_CLONING_FAILED, handleCloningFailed);
      application.eventSystem.internal.off(externalEventCodes.TEMPLATE_CLONING_PROGRESS, handleCloningProgress);

    };

  }

  function initialize() {

    if (properties.clonerId === null) {
      createCloner();
    } else {
      loadCloner();
    }

    return registerEventListeners();

  }

  function retry() {

    HelptapEditor.setAttributes(editor, {
      properties: {
        ...properties,
        retry: false,
        retryMessage: null,
        clonerId: null
      }
    }, elementPath);

  }

  function tryRenderRetryOptions() {

    if (properties.retry !== true) {
      return;
    }

    return <div className="template-cloner-retry"><label>{properties.retryMessage}</label><button onClick={retry}><ReloadIcon /></button></div>;

  }

  function tryWorkingStatus() {

    if (properties.retry === true) {
      return;
    }

    return <div className="template-cloner-spinner">
      <label>{determineWorkingStatus(properties)}</label>
      <ht-spinner size="small" />
    </div>;

  }

  function renderContent() {

    const mainAttributes = {
      ...props.attributes,
      contentEditable: false,
      className: 'template-cloner-content'
    };

    return <div {...mainAttributes}>
      {tryWorkingStatus()}
      {tryRenderRetryOptions()}
      {props.children}
    </div>;

  }

  function renderMain() {

    const baseElementProperties = {
      ...props,
      placeholder: '',
      className: 'template-cloner-main',
      customNodeControls: [
        nodeControls.URL_INPUT
      ],
      disabledNodeControls: true
    };

    return <BaseElement {...baseElementProperties}>
      {renderContent()}
    </BaseElement>;

  }

  return renderMain();

}

export default TemplateCloner;